/**
 * Heilmittelverordnungen (HMV) repository.
 *
 * Owns `heilmittelverordnungen` (the prescriptions themselves) and
 * `hmv_sitzungen` (session counter rows that tick down against the
 * prescribed sessions on each HMV).
 */

import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { unwrap } from '@/lib/supabase-helpers';

export async function listHmv(orgId: string, patientId: string) {
  return unwrap(
    supabase
      .from('heilmittelverordnungen')
      .select('*')
      .eq('organization_id', orgId)
      .eq('patient_id', patientId)
      .order('order_date', { ascending: false }),
  );
}

const CreateHmvSchema = z.object({
  organization_id: z.string().uuid(),
  patient_id: z.string().uuid(),
  created_by: z.string().uuid().nullable(),
  hmv_code: z.string().trim().min(1),
  arzt_name: z.string().trim().nullable(),
  diagnosis: z.string().trim().nullable(),
  icd10_code: z.string().trim().nullable(),
  prescribed_sessions: z.number().int().positive(),
  notes: z.string().trim().nullable(),
  verordnung_typ: z.string().trim().min(1),
  behandlungsbeginn_datum: z.string().nullable(),
});

export type CreateHmvInput = z.infer<typeof CreateHmvSchema>;

export async function createHmv(input: CreateHmvInput) {
  const parsed = CreateHmvSchema.safeParse(input);
  if (!parsed.success) {
    throw new Error(`Ungültige Heilmittelverordnung: ${parsed.error.message}`);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.from('heilmittelverordnungen') as any)
    .insert([parsed.data])
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function setHmvStatus(orgId: string, id: string, status: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.from('heilmittelverordnungen') as any)
    .update({ status })
    .eq('id', id)
    .eq('organization_id', orgId);
  if (error) throw error;
}

// ── Sessions linked to a HMV prescription ──────────────────────────────

export async function listHmvSessions(hmvId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.from('hmv_sitzungen') as any)
    .select('*')
    .eq('verordnung_id', hmvId)
    .order('session_number', { ascending: true });
  if (error) throw error;
  return (data ?? []) as Array<Record<string, unknown>>;
}

const CreateHmvSessionSchema = z.object({
  organization_id: z.string().uuid(),
  heilmittelverordnung_id: z.string().uuid(),
  patient_id: z.string().uuid(),
  created_by: z.string().uuid().nullable(),
  session_number: z.number().int().positive(),
  session_date: z.string().min(1),
  duration_minutes: z.number().int().positive().nullable(),
  notes: z.string().trim().nullable(),
});

export type CreateHmvSessionInput = z.infer<typeof CreateHmvSessionSchema>;

export async function createHmvSession(input: CreateHmvSessionInput) {
  const parsed = CreateHmvSessionSchema.safeParse(input);
  if (!parsed.success) {
    throw new Error(`Ungültige HMV-Sitzung: ${parsed.error.message}`);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase.from('hmv_sitzungen') as any).insert([parsed.data]);
  if (error) throw error;
}
